import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
  faXmark,
  IconDefinition,
} from '@fortawesome/sharp-solid-svg-icons';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import Typography from '../typography';

export enum ToastVariant {
  INFORMATIONAL = 'informational',
  SUCCESS = 'success',
  WARNING_ORANGE = 'warning orange',
  WARNING_YELLOW = 'warning yellow',
  ERROR = 'error',
}

export enum ToastOrientation {
  STACKED = 'stacked',
  INLINE = 'inline',
}

export const variants: Record<ToastVariant, string> = {
  [ToastVariant.INFORMATIONAL]: 'bg-blue-utility-20 border-blue-60',
  [ToastVariant.SUCCESS]: 'bg-green-utility-20 border-green-50',
  [ToastVariant.WARNING_ORANGE]: 'bg-orange-utility-20 border-orange-utility',
  [ToastVariant.WARNING_YELLOW]: 'bg-yellow-utility-20 border-yellow-utility',
  [ToastVariant.ERROR]: 'bg-red-utility-20 border-red-utility',
};

const icons: Record<ToastVariant, { color: string; icon: IconDefinition }> = {
  [ToastVariant.INFORMATIONAL]: { color: 'text-blue-60', icon: faCircleInfo },
  [ToastVariant.SUCCESS]: { color: 'text-green-50', icon: faCircleCheck },
  [ToastVariant.WARNING_ORANGE]: {
    color: 'text-orange-utility',
    icon: faTriangleExclamation,
  },
  [ToastVariant.WARNING_YELLOW]: {
    color: 'text-yellow-utility',
    icon: faTriangleExclamation,
  },
  [ToastVariant.ERROR]: {
    color: 'text-red-utility',
    icon: faCircleExclamation,
  },
};

const wrapperClassnames: Record<ToastOrientation, string> = {
  [ToastOrientation.STACKED]: '',
  [ToastOrientation.INLINE]: 'flex gap-x-1 items-center',
};

interface IProps {
  additionalContent?: ReactNode | null;
  canClose?: boolean;
  className?: string;
  contentClassName?: string;
  iconHidden?: boolean;
  title: string;
  subtitle: string | ReactNode;
  variant?: ToastVariant;
  orientation?: ToastOrientation;
  iconClassName?: string;
  closeButtonClassName?: string;
  onClose?: () => void;
}
const Toast = ({
  additionalContent = null,
  canClose = false,
  className = '',
  contentClassName = '',
  iconHidden = false,
  onClose = undefined,
  orientation = ToastOrientation.STACKED,
  variant = ToastVariant.INFORMATIONAL,
  iconClassName = '',
  closeButtonClassName = '',
  title,
  subtitle,
}: IProps) => {
  return (
    <div
      className={twMerge(
        'flex max-w-full justify-between border-l-[3px] px-[18px] py-4 font-[Roboto] text-gray-100 transition-all',
        variant && variants[variant],
        orientation === ToastOrientation.INLINE && 'items-center',
        className
      )}
    >
      {!iconHidden && (
        <FontAwesomeIcon
          className={twMerge(
            `mr-[18px]`,
            icons[variant as ToastVariant].color,
            iconClassName
          )}
          icon={icons[variant as ToastVariant].icon}
        />
      )}

      <div
        className={twMerge(
          'mr-auto',
          orientation && wrapperClassnames[orientation], // Apply styles based on orientation
          contentClassName
        )}
      >
        <Typography data-testid="base-toast" variant="h6">
          {title}
        </Typography>
        <Typography
          className={clsx(
            additionalContent &&
              orientation === ToastOrientation.STACKED &&
              'mb-6'
          )}
          variant="body2"
        >
          {subtitle}
        </Typography>
        {orientation === ToastOrientation.STACKED && additionalContent}
      </div>
      <div
        className={twMerge(
          'ml-4 flex gap-x-4',
          orientation === ToastOrientation.STACKED && 'items-start'
        )}
      >
        {orientation === ToastOrientation.INLINE && additionalContent}
        {canClose && (
          <button
            className={closeButtonClassName}
            type="button"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Toast;
